import { Alert, Input, Modal, Select } from 'antd';
import { Dispatch, Key, SetStateAction, useEffect, useState } from 'react';
import { useGlobalContext } from '../../../context/GlobalContext';
import { ICategoriaDTO, ICentroCustoDTO, IDespesaEdicaoEmLoteDTO, IFornecedorCleanDTO, ISubCategoriaDTO } from '../../../models/happy-code-api.model';
import { Predicate } from '../../../models/predicate.model';
import { useCategoriaService } from '../../../services/categoria.service';
import { useCentroCustoService } from '../../../services/centro-de-custo.service';
import { useDespesaService } from '../../../services/despesa.service';
import { useFornecedorService } from '../../../services/fornecedor.service';
import { useNotificationService } from '../../../services/notification.service';
import { useSubCategoriaService } from '../../../services/subcategoria.service';
import { toMoment } from '../../../util/format';
import BaseDatePicker from './base-date-picker';
import BaseInput from './base-input';
import BaseSelect from './base-select';
import Styles from './components.module.scss';
import { InboxOutlined } from '@ant-design/icons';
import Dragger from 'antd/lib/upload/Dragger';
import { UploadFile } from 'antd/lib/upload/interface';
import fileList from '../CadastroDespesa/components/file-list';
import { useUploadLote } from '../hooks/use-upload-lote';

interface ModalEdicaoLoteProps {
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	idsDespesas: number[] | Key[];
	refreshTable: () => void;
	setSelectedRowKeys: Dispatch<SetStateAction<Key[]>>;
}

export default function ModalEdicaoLote({ open, setOpen, idsDespesas, refreshTable, setSelectedRowKeys }: ModalEdicaoLoteProps) {
	const [dto, setDto] = useState<IDespesaEdicaoEmLoteDTO>();
	const [categorias, setCategorias] = useState<ICategoriaDTO[]>([]);
	const [centros, setCentros] = useState<ICentroCustoDTO[]>([]);
	const [fornecedores, setFornecedores] = useState<IFornecedorCleanDTO[]>([]);
	const [subCategorias, setSubCategoria] = useState<ISubCategoriaDTO[]>([]);

	const { unidade } = useGlobalContext();
	const categoriaService = useCategoriaService();
	const centroCustoService = useCentroCustoService();
	const fornecedorService = useFornecedorService();
	const subCategoriaService = useSubCategoriaService();
	const despesaService = useDespesaService();
	const notification = useNotificationService();
	const { setIsGlobalLoading } = useGlobalContext();
	const { fileList, setFileList, uploadAll } = useUploadLote(dto);

	const fetchFornecedores = () => {
		const predicate = new Predicate();
		predicate.addOption('unidade.id', unidade?.id);
		fornecedorService.findList(predicate).then(({ data }) => setFornecedores(data));
	};

	const fetchCentroCusto = () => {
		centroCustoService.findList().then(({ data }) => setCentros(data));
	};

	const fetchCategorias = () => {
		const predicate = new Predicate();
		predicate.addOption('ativo', true);
		categoriaService.findList(predicate).then(({ data }) => setCategorias(data));
	};

	const fetchSubCategorias = () => {
		const predicate = new Predicate();
		setIsGlobalLoading(true);
		predicate.addOption('categoria.id', dto.idCategoria);
		subCategoriaService
			.findList(predicate)
			.then(({ data }) => setSubCategoria(data))
			.finally(() => setIsGlobalLoading(false));
	};

	const loadOptions = async () => {
		const predicate = new Predicate();
		const predicateUnidade = new Predicate();
		predicate.addOption('ativo', true);

		predicateUnidade.addOption('ativo', true);
		predicateUnidade.addOption('unidade.id', unidade.id);

		setIsGlobalLoading(true);
		try {
			await Promise.all([fetchFornecedores(), fetchCentroCusto(), fetchCategorias()]);
		} finally {
			setIsGlobalLoading(false);
		}
	};

	useEffect(() => {
		loadOptions();
	}, []);

	useEffect(() => {
		if (dto?.idCategoria) {
			fetchSubCategorias();
		}
	}, [dto?.idCategoria]);

	const handleOk = async () => {
		if (dto?.idCategoria && !dto?.idSubCategoria) {
			return notification({
				description: 'Selecione uma subcategoria.',
				type: 'warning',
				message: 'Aviso!',
			});
		}
		setIsGlobalLoading(true);

		const arquivos = await uploadAll();
		const newDtoWithArquivos: IDespesaEdicaoEmLoteDTO = {
			...dto,
			idsDespesas: idsDespesas as number[],
			arquivos,
		};
		despesaService.editarEmLote(newDtoWithArquivos).finally(() => {
			notification({
				description: 'Despesas alteradas com sucesso!',
				type: 'success',
				message: 'Sucesso',
			});
			setOpen(false);
			setSelectedRowKeys([]);
			refreshTable();
			setIsGlobalLoading(false);
		});
	};

	return (
		<Modal
			destroyOnClose
			title="Edição em lote"
			visible={open}
			onOk={handleOk}
			onCancel={() => setOpen(false)}
			okText="Salvar alterações"
			cancelText="Cancelar"
		>
			<div className={Styles.modalContainer}>
				<Alert
					closable
					showIcon
					message="Edição em lote"
					description={
						<>
							<ul style={{ margin: 0, paddingLeft: 20 }}>
								<li style={{listStyleType: 'disc'}}>
									Apenas algumas opções são disponíveis para alteração em lote. Em caso de lançamentos recorrentes, somente os lançamentos selecionados serão
									atualizados.
								</li>
								<li style={{listStyleType: 'disc'}}>As informações referentes aos anexos e observações serão refletidas apenas nas parcelas selecionadas.</li>
							</ul>
						</>
					}
					type="info"
				/>
				<BaseSelect
					value={dto?.idFornecedor}
					onChange={(id) => setDto({ ...dto, idFornecedor: id })}
					labelConfig={{ label: 'Fornecedor' }}
					placeholder="Selecione"
					width="100%"
				>
					{fornecedores.map((f) => (
						<Select.Option key={f.id} value={f.id}>
							{f.nomeFantasia}
						</Select.Option>
					))}
				</BaseSelect>

				<BaseDatePicker
					value={dto?.dataCompetencia ? toMoment(dto.dataCompetencia) : undefined}
					onChange={(d) => setDto({ ...dto, dataCompetencia: d?.toDate() })}
					labelConfig={{ label: 'Data de lançamento' }}
					placeholder="Selecione uma data"
					width="100%"
				/>

				<BaseSelect
					value={dto?.idCategoria}
					onChange={(id) => setDto({ ...dto, idCategoria: id })}
					labelConfig={{ label: 'Categoria' }}
					placeholder="Selecione"
					width="100%"
				>
					{categorias &&
						categorias.map((c) => (
							<Select.Option key={c.id} value={c.id} labelConfig={undefined}>
								{c.dsCategoria}
							</Select.Option>
						))}
				</BaseSelect>

				<BaseSelect
					disabled={!dto?.idCategoria}
					value={dto?.idSubCategoria}
					onChange={(id) => setDto({ ...dto, idSubCategoria: id })}
					labelConfig={{ label: 'SubCategoria' }}
					placeholder="Selecione"
					width="100%"
				>
					{subCategorias &&
						subCategorias.map((c) => (
							<Select.Option key={c.id} value={c.id} labelConfig={undefined}>
								{c.dsSubCategoria}
							</Select.Option>
						))}
				</BaseSelect>
				<BaseSelect
					value={dto?.idCentroCusto}
					onChange={(id) => setDto({ ...dto, idCentroCusto: id })}
					labelConfig={{ label: 'Centro de custo' }}
					placeholder="Selecione"
					width="100%"
				>
					{centros.map((c) => (
						<Select.Option key={c.id} value={c.id} labelConfig={undefined}>
							{c.dsCentroCusto}
						</Select.Option>
					))}
				</BaseSelect>
				<div style={{ width: '100%', marginTop: '1rem' }}>
					<p>
						<strong>Anexar arquivos</strong>
					</p>
					<Dragger
						listType="text"
						multiple
						customRequest={({ file, onSuccess }) => {
							setTimeout(() => onSuccess?.('ok'), 0);
						}}
						onChange={({ fileList }) => setFileList(fileList as UploadFile[])}
						fileList={fileList as UploadFile[]}
						itemRender={(originNode, file, files, { remove }) => (
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<div
									style={{
										minWidth: 200,
										maxWidth: 200,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{originNode}
								</div>
								<div style={{ flex: 1, padding: 10 }}>
									<Input
										placeholder="Descrição"
										maxLength={100}
										value={(file as any).description || ''}
										onChange={(e) => {
											const desc = e.target.value;
											setFileList((prev) => prev.map((f) => (f.uid === file.uid ? { ...f, description: desc } : f)));
										}}
									/>
								</div>
							</div>
						)}
					>
						<p className="ant-upload-drag-icon">
							<InboxOutlined rev={undefined} />
						</p>
						<p className="ant-upload-text">Clique ou arraste o arquivo para esta área</p>
						<p className="ant-upload-hint">Tamanho máximo do arquivo suportado: 20MB</p>
					</Dragger>
				</div>
				<BaseInput
					value={dto?.descricao}
					onChange={(e) => setDto({ ...dto, descricao: e.target.value })}
					labelConfig={{ label: 'Observações' }}
					placeholder="Descreva observações relevantes sobre essa parcela"
					width="100%"
				/>
			</div>
		</Modal>
	);
}
